/* eslint-disable max-lines */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { buildUrlActionTrigger } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import { JobEditorDialogPathsMap, JobEditorDialogTab } from '@core/offers-types';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  Step,
  WalkthroughDialogComponent,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  walkthroughSanitizedVideoResolver,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
  WalkthroughType,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class CreateJobWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);

  private readonly hasJobTemplatesFeature = this.featureSpecDataHelpers.selectIsEnabled(
    FeatureCode.JobTemplates,
  );
  private readonly hasWorkflowAutomations = injectDevFlag(DevFlag.WorkflowAutomations);

  public readonly subtask = GettingStartedSubtask.CreateJob;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-create-job-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_1.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_1.description',
          ),
          videoUrl: walkthroughSanitizedVideoResolver('https://www.youtube.com/embed/rHCOtqQup_8'),
          ctaText: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_1.cta_text'),
          ctaHandler: () => {
            this.navigateToNextStep(
              this.hasJobTemplatesFeature()
                ? 'walkthrough-step-create-job-2'
                : 'walkthrough-step-create-job-3',
            );
          },
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-create-job-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['top', 'right'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_2_v2.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_2_v2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: -16 },
      },
      isHidden: !this.hasJobTemplatesFeature(),
    },
    {
      id: 'walkthrough-step-create-job-3',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_2.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-create-job-4',
      position: 4,
      walkThroughType: WalkthroughType.Jobs,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_3.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_3.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-create-job-5',
      position: 5,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_4.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-create-job-6',
      position: 6,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_5.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
          ctaHandler: () => {
            this.navigateToNextStep(
              this.hasWorkflowAutomations
                ? 'walkthrough-step-create-job-7'
                : 'walkthrough-step-create-job-8',
            );
          },
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    this.hasWorkflowAutomations
      ? {
          id: 'walkthrough-step-create-job-7',
          position: 7,
          component: {
            definition: WalkthroughTooltipComponent,
            inputs: {
              arrowConfig: ['top', 'right'],
              heading: this.i18n.translate(
                'app.walkthrough.walkthroughs.create_job.step_6_v2.heading',
              ),
              description: this.i18n.translate(
                'app.walkthrough.walkthroughs.create_job.step_6_v2.description',
              ),
              ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
              ctaIcon: 'chevron-right',
            },
          },
          renderOptions: {
            strategy: flexiblePositionStrategyFactory,
            useWalkthroughBackdropClass: true,
            positions: [
              {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'top',
                offsetX: -10,
                offsetY: -20,
              },
            ],
          },
        }
      : {
          id: 'walkthrough-step-create-job-7',
          position: 7,
          component: {
            definition: WalkthroughTooltipComponent,
            inputs: {
              arrowConfig: ['middle', 'left'],
              heading: this.i18n.translate(
                'app.walkthrough.walkthroughs.create_job.step_6.heading',
              ),
              description: this.i18n.translate(
                'app.walkthrough.walkthroughs.create_job.step_6.description',
              ),
              ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
              ctaIcon: 'chevron-right',
            },
          },
          renderOptions: {
            strategy: flexiblePositionStrategyFactory,
            useWalkthroughBackdropClass: true,
            positions: [
              { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
            ],
            offset: { offsetX: 24, offsetY: 0 },
          },
        },
    {
      id: 'walkthrough-step-create-job-8',
      position: 8,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_7.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_7.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-create-job-9',
      position: 9,
      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_8.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.create_job.step_8.description',
          ),
          ctaText: this.i18n.translate('app.walkthrough.walkthroughs.create_job.step_8.cta_text'),
          helpArticleKey: 'createJobs',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.getFilteredSteps())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(
          state,
          this.subtask,
          ['offers'],
          false,
          true,
        );
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  public navigateToNextStep(step: Step['id'] | null): void {
    const id = 'draft';
    switch (step) {
      case 'walkthrough-step-create-job-2':
        this.router.navigateByUrl(buildUrlActionTrigger('CreateJobOpen', null, true));
        break;
      case 'walkthrough-step-create-job-5':
        this.router.navigate(this.getPathToNewJobEditor(id, JobEditorDialogTab.Application));
        break;

      case 'walkthrough-step-create-job-7':
        this.router.navigate(this.getPathToNewJobEditor(id, JobEditorDialogTab.Process));
        break;

      case 'walkthrough-step-create-job-8':
        this.router.navigate(this.getPathToNewJobEditor(id, JobEditorDialogTab.Team));
        break;

      default:
        this.router.navigate(this.getPathToNewJobEditor(id, JobEditorDialogTab.JobDetails));
        break;
    }
  }

  private getFilteredSteps(): Step[] {
    return this.steps
      .filter(step => !step.isHidden)
      .map((step, i) => ({ ...step, position: i + 1 }));
  }

  private finishHandler(): void {
    this.router
      .navigate([{ outlets: { 'job-editor': null } }])
      .then(() => this.router.navigate(['offers']))
      .then(() => this.router.navigateByUrl(buildUrlActionTrigger('CreateJobOpen', null, true)));
  }

  private getPathToNewJobEditor(jobId: string, tab: JobEditorDialogTab) {
    return [
      {
        outlets: {
          'job-editor': [jobId, JobEditorDialogPathsMap[tab]],
        },
      },
    ];
  }
}
